import { createContext, useContext, useMemo } from "react";

import type { ApiScope } from "@/api/createApiClient";
import { createApiClient } from "@/api/createApiClient";
import type { Api } from "@/api/types";
import { useConfig } from "@/providers/config/useConfig";

type ApiClientContextValue = ApiScope & { api: Api<null> };
const ApiClientContext = createContext<ApiClientContextValue | null>(null);

export function useApiClient(): ApiClientContextValue {
  const context = useContext(ApiClientContext);
  if (!context) {
    throw new Error("useApiClient must be used within an ApiClientProvider");
  }

  return context;
}

/** Supplies an immutable request scope. Nested scopes inherit credentials/branding, never a mutable current project. */
export function ApiClientProvider({ children, ...scope }: React.PropsWithChildren<ApiScope>): React.ReactNode {
  const parent = useContext(ApiClientContext);
  const baseUrl = useConfig("newCoreApiRootUri");
  const projectId = scope.projectId;
  const token = scope.token ?? parent?.token;
  const appBranding = scope.appBranding ?? parent?.appBranding;

  // Query functions retain this client across retries. Its headers must keep the scope
  // in which the function was created, even after navigation changes the current scope.
  const api = useMemo(() => {
    if (!baseUrl) {
      throw new Error("No API base URL configured");
    }

    return createApiClient(baseUrl, { projectId, token, appBranding });
  }, [baseUrl, projectId, token, appBranding]);

  return <ApiClientContext value={{ api, projectId, token, appBranding }}>{children}</ApiClientContext>;
}
